import React from 'react';
import { useTranslation } from 'react-i18next';

interface LoadingErrorStateProps {
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  /** type of skeleton: 'grid' for cards, 'table' for table rows */
  type?: 'grid' | 'table';
  /** number of skeleton items to show */
  count?: number;
  onRetry?: () => void; // optional retry callback
}

const LoadingErrorState: React.FC<LoadingErrorStateProps> = ({
  isLoading,
  isError,
  errorMessage,
  type = '',
  count = 6,
  onRetry,
}) => {
  const {t} = useTranslation();
  if (isLoading) {
    if (type === 'grid') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: count }).map((_, idx) => (
            <div
              key={idx}
              className="animate-pulse bg-gray-200 h-48 rounded-lg"
            />
          ))}
        </div>
      );
    }

    if (type === 'table') {
      return (
        <div className="space-y-4 w-full">
          {Array.from({ length: count }).map((_, idx) => (
            <div
              key={idx}
              className="animate-pulse grid grid-cols-4 gap-4 p-2 bg-gray-200 rounded w-full h-[60px] items-center"
            >
              <div className="h-8 bg-gray-300 rounded w-full" />
              <div className="h-8 bg-gray-300 rounded w-full" />
              <div className="h-8 bg-gray-300 rounded w-full" />
              <div className="h-8 bg-gray-300 rounded w-full" />
            </div>
          ))}
        </div>
      );
    }

    // default spinner fallback
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center mt-5 bg-red-50 border border-red-200 rounded-2xl p-6 space-y-4 shadow-sm text-center">
        <svg
          className="w-12 h-12 text-red-600"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z"
          />
        </svg>
        <h3 className="text-lg font-bold text-red-600">{t('error_title')}</h3>
        <p className="text-red-500">{errorMessage || t('error_message')}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            {t("retry")}
          </button>
        )}
      </div>
    );
  }

  return null;
};

export default LoadingErrorState;
